import React, { useState, useEffect } from 'react'
import { T, Spinner, Empty, pct, probColor, SearchBar, searchMatch, ScrollRow, Avatar, bookName, fmtOdds, tdColor, inGameFilter } from '../ui'
import { getNFLTdLab, getNFLRedZoneSpots, getNFLRedZoneRecord, getNFLDueTds } from '../api'
import { shareNflPaydirt } from '../share'
import { legKey } from '../parlay'
import { nickify, teamNick } from '../plain'
import { useOddsRange, inOddsRange } from '../oddsfilter'

const RZ_RED = '#ff5a52'
const RZ_RED_DIM = 'rgba(255,90,82,0.13)'

// ── Red Zone Power Spots ──────────────────────────────────────────────────────
// The curated highlight board (the NFL answer to baseball's Power Spots): the week's
// best anytime-TD SETUPS, ranked by a composite of the three levers that decide who
// scores inside the 20 — opportunity (goal-line / RZ role) × matchup (opponent RZ
// defense) × environment (implied points). Each card leads with a 0-100 Red Zone Score
// and a plain-English "why", with the components as chips. Tap opens the player.
function RzChip({ label, value, tone }) {
  const col = tone === 'good' ? T.green : tone === 'bad' ? T.amber : T.muted2
  return (
    <span style={{ fontSize: 10.5, fontWeight: 700, color: col, background: T.card2,
      border: `1px solid ${T.border}`, borderRadius: 6, padding: '3px 7px', whiteSpace: 'nowrap' }}>
      <span style={{ color: T.muted, fontWeight: 400 }}>{label} </span>{value}
    </span>
  )
}

// Below this chance-to-score, a spot is a LONGSHOT: the SETUP can grade elite (great role +
// soft matchup) while the model still makes him unlikely to actually find the end zone — a
// low-volume pass-catcher in a low-total game. We flag it so a great *spot* can't masquerade
// as a high-confidence *play*.
const LONGSHOT_P = 0.45

function RzCard({ r, rank, onPlayer, inSlip, onToggle }) {
  const p = r.td_prob || 0
  const longshot = p < LONGSHOT_P
  const hot = rank <= 3 && !longshot          // the red "top spot" treatment is for real plays, not darts
  const softRank = r.opp_rz_rank != null && r.opp_rz_of
    ? `#${r.opp_rz_rank}/${r.opp_rz_of}` : null
  const open = () => onPlayer && onPlayer({
    gsis_id: r.gsis_id, full_name: r.name, pos: r.pos, team: r.team, opp: r.opp, headshot: r.headshot,
    market: 'Anytime TD', prob_over: p, status: 'active',
  })
  return (
    <button onClick={open} style={{ display: 'block', width: '100%', textAlign: 'left', cursor: 'pointer',
      color: T.text, background: T.card, border: `1px solid ${inSlip ? T.green : hot ? RZ_RED : T.border}`,
      borderRadius: 12, padding: 12, marginBottom: 8, opacity: longshot ? 0.9 : 1 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: 13, fontWeight: 900, color: hot ? RZ_RED : T.muted, fontFamily: T.mono, width: 20, flexShrink: 0 }}>#{rank}</span>
        <Avatar url={r.headshot} name={r.name} team={r.team} size={34} />
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 14.5, fontWeight: 800, lineHeight: 1.2 }}>{r.name}</span>
            {longshot && <span style={{ fontSize: 10, fontWeight: 900, letterSpacing: '0.04em', color: T.amber,
              background: 'rgba(245,158,11,0.14)', border: `1px solid ${T.amber}66`, borderRadius: 5, padding: '2px 5px', flexShrink: 0 }}>LONGSHOT</span>}
          </div>
          <div style={{ fontSize: 10.5, color: T.muted }}>{r.pos} · {teamNick(r.team)} vs {teamNick(r.opp)}</div>
        </div>
        {/* SETUP — the 0-100 spot-quality composite (role × matchup × environment), NOT a probability */}
        <div style={{ textAlign: 'center', flexShrink: 0 }}>
          <div style={{ fontFamily: T.mono, fontWeight: 900, fontSize: 19, lineHeight: 1, color: RZ_RED }}>{r.rz_score}</div>
          <div style={{ fontSize: 10, color: T.muted, letterSpacing: '0.05em' }}>SETUP</div>
        </div>
        {/* SCORES — the model's actual chance he finds the end zone */}
        <div style={{ textAlign: 'right', flexShrink: 0, minWidth: 44 }}>
          <div style={{ fontFamily: T.mono, fontWeight: 800, fontSize: 15, lineHeight: 1, color: tdColor(p) }}>{Math.round(p * 100)}%</div>
          <div style={{ fontSize: 10, color: T.muted, letterSpacing: '0.05em' }}>SCORES</div>
        </div>
        <span role="button" onClick={e => { e.stopPropagation(); onToggle && onToggle(r) }}
          title={inSlip ? 'Remove from parlay' : 'Add to parlay'}
          style={{ width: 28, height: 28, borderRadius: 8, flexShrink: 0, display: 'flex', alignItems: 'center',
            justifyContent: 'center', fontSize: 15, fontWeight: 900, cursor: 'pointer',
            background: inSlip ? T.green : T.greenDim, color: inSlip ? '#04140D' : T.green,
            border: `1px solid ${T.green}` }}>{inSlip ? '✓' : '+'}</span>
      </div>
      {r.why && <div style={{ fontSize: 11, color: T.muted2, margin: '8px 0 0', lineHeight: 1.4 }}>{nickify(r.why)}</div>}
      {/* Bet-it-to-win row: best price + where + the model's edge vs that number. */}
      {r.book_odds != null && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, fontWeight: 800, color: T.text, fontFamily: T.mono }}>{fmtOdds(r.book_odds)}</span>
          {r.book && <span style={{ fontSize: 10.5, color: T.muted }}>@ {bookName(r.book)}</span>}
          {r.edge_pct != null && Math.abs(r.edge_pct) >= 0.5 && (
            <span style={{ fontSize: 11, fontWeight: 800, padding: '3px 8px', borderRadius: 6,
              color: r.edge_pct > 0 ? '#04140D' : T.amber,
              background: r.edge_pct > 0 ? T.green : 'transparent',
              border: r.edge_pct > 0 ? 'none' : `1px solid ${T.amber}` }}>
              {r.edge_pct > 0 ? '✓ VALUE ' : ''}{r.edge_pct > 0 ? '+' : ''}{r.edge_pct.toFixed(1)}%
            </span>
          )}
        </div>
      )}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 7 }}>
        {r.gl_share != null && <RzChip label="Goal-line" value={`${Math.round(r.gl_share * 100)}%`} tone={r.gl_share >= 0.4 ? 'good' : null} />}
        {r.rz_share != null && r.rz_share > 0 && <RzChip label="RZ touches" value={`${Math.round(r.rz_share * 100)}%`} tone={r.rz_share >= 0.2 ? 'good' : null} />}
        {softRank && <RzChip label={`${r.opp} RZ D`} value={softRank} tone={r.opp_rz_rank <= Math.max(8, (r.opp_rz_of || 32) / 4) ? 'good' : r.opp_rz_rank >= (r.opp_rz_of || 32) * 0.72 ? 'bad' : null} />}
        {r.implied_pts != null && <RzChip label="Team total" value={r.implied_pts} tone={r.implied_pts >= 26 ? 'good' : r.implied_pts <= 18 ? 'bad' : null} />}
      </div>
    </button>
  )
}

const RZ_SORTS = [
  ['rz_score', 'Best setup'],
  ['td_prob', 'Most likely'],
  ['edge_pct', 'Best value'],
]

// Track record — frozen weekly boards graded vs who actually scored. The proof layer:
// hit rate, ROI at the frozen price, the +EV subset, calibration, and recent results.
function RzKpi({ n, k, tone }) {
  const col = tone === 'good' ? T.green : tone === 'bad' ? T.amber : T.text
  return (
    <div style={{ background: T.card2, border: `1px solid ${T.border}`, borderRadius: 10, padding: '10px 6px', textAlign: 'center' }}>
      <div style={{ fontFamily: T.mono, fontWeight: 900, fontSize: 17, lineHeight: 1, color: col }}>{n}</div>
      <div style={{ fontSize: 10, color: T.muted, letterSpacing: '0.05em', marginTop: 4, textTransform: 'uppercase' }}>{k}</div>
    </div>
  )
}

function RedZoneRecord({ season }) {
  const [rec, setRec] = useState(null)
  const [open, setOpen] = useState(false)
  useEffect(() => {
    if (!open || rec) return
    let alive = true
    getNFLRedZoneRecord(season).then(d => { if (alive) setRec(d) })
    return () => { alive = false }
  }, [open, season, rec])
  const o = rec && rec.overall
  const graded = o && o.plays > 0
  return (
    <div style={{ borderTop: `1px solid ${T.border}`, marginTop: 4, paddingTop: 8 }}>
      <button onClick={() => setOpen(v => !v)} style={{ display: 'flex', width: '100%', alignItems: 'center',
        justifyContent: 'space-between', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 0' }}>
        <span style={{ fontSize: 12.5, fontWeight: 800, color: T.muted2 }}>📊 Track record{graded ? ` · ${o.wins}-${o.losses}` : ''} <span style={{ fontSize: 10, fontWeight: 800, color: T.amber, border: `1px solid ${T.amber}66`, borderRadius: 6, padding: '1px 6px', marginLeft: 4, letterSpacing: 0.5, textTransform: 'uppercase' }}>In testing</span></span>
        <span style={{ color: T.muted, fontSize: 13, fontWeight: 800 }}>{open ? '▾' : '▸'}</span>
      </button>
      {open && (!rec ? <div style={{ padding: 8 }}><Spinner /></div> : !graded ? (
        <div style={{ fontSize: 11, color: T.muted, padding: '8px 2px', lineHeight: 1.5 }}>
          No graded weeks yet — the board freezes at lock each week and grades against who scored. The record fills in as weeks settle.
        </div>
      ) : (
        <div style={{ paddingTop: 8 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 7, marginBottom: 9 }}>
            <RzKpi n={`${o.wins}-${o.losses}`} k="Record" tone="good" />
            <RzKpi n={o.hit_rate != null ? `${o.hit_rate}%` : '—'} k="To-score hit" tone={o.hit_rate >= 50 ? 'good' : null} />
          </div>
          {rec.value && rec.value.plays > 0 && (
            <div style={{ fontSize: 11, color: T.muted2, background: T.greenDim, border: `1px solid ${T.greenMid}`,
              borderRadius: 8, padding: '8px 10px', marginBottom: 9, lineHeight: 1.5 }}>
              <b style={{ color: T.green }}>✓ VALUE plays: {rec.value.hit_rate != null ? `${rec.value.hit_rate}%` : '—'} hit</b>
              {' '}<span style={{ color: T.muted }}>({rec.value.plays} +EV plays — the subset that beat the book)</span>
            </div>
          )}
          {!!(rec.calibration || []).length && (
            <div style={{ fontSize: 10.5, color: T.muted, marginBottom: 9, lineHeight: 1.5 }}>
              Calibration: {rec.calibration.map(b => `shown ${b.pred}% → hit ${b.actual}% (n${b.n})`).join(' · ')}
            </div>
          )}
          {!!(rec.recent || []).length && <>
            <div style={{ fontSize: 10, color: T.faint, letterSpacing: '0.1em', fontWeight: 700, margin: '4px 0 4px' }}>RECENT GRADED</div>
            {rec.recent.slice(0, 10).map((r, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', borderTop: `1px solid ${T.border}` }}>
                <div style={{ flex: 1, minWidth: 0, fontSize: 12.5, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {r.name} <span style={{ fontSize: 10, color: T.muted, fontWeight: 400 }}>{r.pos} · {r.team} · Wk{r.week}</span>
                </div>
                {r.value && <span style={{ fontSize: 10, fontWeight: 800, color: T.green }}>✓</span>}
                <span style={{ fontFamily: T.mono, fontSize: 11, color: T.muted, width: 46, textAlign: 'right' }}>{r.book_odds != null ? (r.book_odds > 0 ? `+${r.book_odds}` : r.book_odds) : ''}</span>
                <span style={{ fontSize: 10, fontWeight: 800, padding: '3px 8px', borderRadius: 6, width: 46, textAlign: 'center',
                  color: r.result === 'hit' ? '#04140D' : T.amber, background: r.result === 'hit' ? T.green : 'transparent',
                  border: r.result === 'hit' ? 'none' : `1px solid ${T.amber}` }}>{r.result === 'hit' ? 'HIT' : 'MISS'}</span>
              </div>
            ))}
          </>}
        </div>
      ))}
    </div>
  )
}

// A prominent highlight banner for a strong recent Pay Dirt week (e.g. "9-1 in Week 4").
// Shows only when the newest graded week cleared a real bar — a quiet week shows nothing.
function PayDirtHighlight({ season }) {
  const [wk, setWk] = useState(null)
  useEffect(() => { let a = true
    getNFLRedZoneRecord(season).then(d => { if (a) setWk((d?.by_week || [])[0] || null) }).catch(() => {})
    return () => { a = false } }, [season])
  if (!wk || wk.plays < 5 || (wk.hit_rate || 0) < 60) return null
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: T.green, color: '#04140D',
      borderRadius: 11, padding: '10px 13px', marginBottom: 12, fontWeight: 900 }}>
      <span style={{ fontSize: 20 }}>🎯</span>
      <span style={{ flex: 1, fontSize: 13.5, lineHeight: 1.3 }}>
        Pay Dirt went <span style={{ fontFamily: T.mono }}>{wk.wins}-{wk.losses}</span> in Week {wk.week}
        <span style={{ fontWeight: 700 }}> · {wk.hit_rate}% to score</span>
      </span>
    </div>
  )
}

function RedZoneSpots({ season, week, onPlayer, addTd, slipKeys, gameFilter }) {
  const oddsRange = useOddsRange()
  const [data, setData] = useState(null)
  const [open, setOpen] = useState(false)   // collapsed by default — user taps to expand
  const [sharing, setSharing] = useState(false)
  const [sort, setSort] = useState('rz_score')
  const [posF, setPosF] = useState('ALL')     // position filter (incl. QB rushing)
  const [teamF, setTeamF] = useState('ALL')   // team filter
  useEffect(() => {
    let alive = true
    // Pull a deeper pool so the team/position filters have something to work with; we still
    // only SHOW the top 10 of whatever's selected (Pay Dirt stays a top-10 board).
    getNFLRedZoneSpots(season, week, 40).then(d => { if (alive) setData(d) })
    return () => { alive = false }
  }, [season, week])
  if (!data) return null
  const spots = (data.spots || []).filter(s => inGameFilter(gameFilter, s.team) && inOddsRange(oddsRange, s.book_odds ?? s.odds))
  if (!spots.length) return null
  const inSlip = slipKeys || new Set()
  const teams = [...new Set(spots.map(s => s.team).filter(Boolean))].sort()
  // Client-side re-sort of the board so a user can hunt by matchup, by raw likelihood, or by
  // VALUE (edge vs the book) — the last is what maximizes long-run wins — then filter by
  // position (QB surfaces QB-rushing goal-line spots) and team, and show the top 10.
  const sorted = spots.slice().sort((a, b) => (b[sort] ?? -999) - (a[sort] ?? -999))
  const filtered = sorted.filter(r =>
    (posF === 'ALL' || (r.pos || '').toUpperCase() === posF) &&
    (teamF === 'ALL' || r.team === teamF))
  const shown = filtered.slice(0, 10)
  return (
    <div style={{ background: RZ_RED_DIM, border: `1px solid ${RZ_RED}55`, borderRadius: 14,
      padding: '12px 12px 6px', marginBottom: 14 }}>
      <PayDirtHighlight season={season} />
      <button onClick={() => setOpen(o => !o)} style={{ display: 'flex', width: '100%', alignItems: 'center',
        justifyContent: 'space-between', background: 'none', border: 'none', cursor: 'pointer', padding: 0, marginBottom: open ? 8 : 2 }}>
        <div style={{ textAlign: 'left' }}>
          <div style={{ fontSize: 15, fontWeight: 900, color: T.text, letterSpacing: '-0.01em' }}>🏈 Pay Dirt</div>
          <div style={{ fontSize: 10.5, color: T.muted, marginTop: 1 }}>The week's best red-zone TD setups · role × matchup × implied points · with the best price to bet it</div>
        </div>
        <span style={{ color: RZ_RED, fontSize: 15, fontWeight: 800 }}>{open ? '▾' : '▸'}</span>
      </button>
      {open && <>
        <button onClick={async () => { if (sharing) return; setSharing(true); try { await shareNflPaydirt(season, week) } catch (_) {} setSharing(false) }}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, width: '100%', cursor: 'pointer',
            background: RZ_RED_DIM, border: `1px solid ${RZ_RED}66`, color: RZ_RED, borderRadius: 10, padding: '9px 12px',
            fontSize: 12.5, fontWeight: 800, marginBottom: 8 }}>
          {sharing ? 'Building graphic…' : '🖼️ Export as graphic'}
        </button>
        <ScrollRow style={{ paddingBottom: 8, marginBottom: 6, alignItems: 'center' }}>
          <span style={{ fontSize: 10.5, color: T.muted, alignSelf: 'center', flexShrink: 0 }}>Sort</span>
          {RZ_SORTS.map(([k, lbl]) => {
            const on = k === sort
            return (
              <button key={k} onClick={() => setSort(k)}
                style={{ flexShrink: 0, padding: '5px 10px', borderRadius: 8, cursor: 'pointer', whiteSpace: 'nowrap',
                  border: `1px solid ${on ? RZ_RED : T.border}`, background: on ? RZ_RED_DIM : T.card,
                  color: on ? RZ_RED : T.muted, fontSize: 11, fontWeight: 700 }}>{lbl}</button>
            )
          })}
        </ScrollRow>
        <ScrollRow style={{ paddingBottom: 8, marginBottom: 6, alignItems: 'center' }}>
          <span style={{ fontSize: 10.5, color: T.muted, alignSelf: 'center', flexShrink: 0 }}>Pos</span>
          {['ALL', 'QB', 'RB', 'WR', 'TE'].map(k => {
            const on = k === posF
            return (
              <button key={k} onClick={() => setPosF(k)}
                style={{ flexShrink: 0, padding: '5px 10px', borderRadius: 8, cursor: 'pointer', whiteSpace: 'nowrap',
                  border: `1px solid ${on ? RZ_RED : T.border}`, background: on ? RZ_RED_DIM : T.card,
                  color: on ? RZ_RED : T.muted, fontSize: 11, fontWeight: 700 }}>{k === 'ALL' ? 'All' : k}</button>
            )
          })}
        </ScrollRow>
        {teams.length > 1 && (
          <ScrollRow style={{ paddingBottom: 8, marginBottom: 6, alignItems: 'center' }}>
            <span style={{ fontSize: 10.5, color: T.muted, alignSelf: 'center', flexShrink: 0 }}>Team</span>
            {['ALL', ...teams].map(k => {
              const on = k === teamF
              return (
                <button key={k} onClick={() => setTeamF(k)}
                  style={{ flexShrink: 0, padding: '5px 10px', borderRadius: 8, cursor: 'pointer', whiteSpace: 'nowrap',
                    border: `1px solid ${on ? RZ_RED : T.border}`, background: on ? RZ_RED_DIM : T.card,
                    color: on ? RZ_RED : T.muted, fontSize: 11, fontWeight: 700 }}>{k === 'ALL' ? 'All' : k}</button>
              )
            })}
          </ScrollRow>
        )}
        {shown.length ? shown.map((r, i) => <RzCard key={`${r.gsis_id}:${r.pos}`} r={r} rank={i + 1} onPlayer={onPlayer}
          inSlip={inSlip.has(`${r.gsis_id}:anytime_td`)} onToggle={addTd} />)
          : <div style={{ fontSize: 11, color: T.muted, padding: '8px 2px 10px' }}>No red-zone spots match that filter.</div>}
        <div style={{ fontSize: 10, color: T.muted, padding: '2px 2px 6px', lineHeight: 1.45 }}>
          {!data.rz_data && 'Red-zone role data still loading — spots ranked by matchup + team total for now. '}
          <b style={{ color: RZ_RED }}>SETUP</b> = spot quality 0–100 (role × matchup × total); <b style={{ color: T.text }}>SCORES</b> = model's chance he finds the end zone — a big SETUP with a low SCORES (<span style={{ color: T.amber }}>LONGSHOT</span>) is a great spot at long odds, not a lock. Sorted by SETUP by default — tap <b style={{ color: T.text }}>Most likely</b> to rank by chance to score. <span style={{ color: T.green }}>✓ VALUE</span> = the model beats the book's price (+EV). Research only · 21+.
        </div>
        <RedZoneRecord season={season} />
      </>}
    </div>
  )
}

// TD Lab — the anytime-TD command center. Every candidate with all the scoring
// signals (opportunity · matchup · environment) in one place, filterable to the
// exact profile a sharp TD bettor hunts for, and sortable on any of them.

const SORTS = [
  ['prob', 'To score'], ['exp_td', 'Exp TD'], ['rz_share', 'RZ share'],
  ['gl_touches', 'Goal line'], ['implied_pts', 'Team total'],
]
const POSITIONS = [['ALL', 'All'], ['RB', 'RB'], ['WR', 'WR'], ['TE', 'TE'], ['QB', 'QB']]
const FILTERS = [
  ['gl', '🎯 Goal-line role', r => (r.gl_touches || 0) >= 1],
  ['soft', '🟢 Soft matchup', r => r.def_td_rank != null && r.def_td_rank <= 10],
  ['total', '📈 High team total', r => (r.implied_pts || 0) >= 24],
  ['calm', '🏟 Calm / dome', r => r.wind == null || r.wind < 10],
]

// One color-coded signal chip. tone: 'good' | 'bad' | null(neutral).
function Sig({ label, value, tone }) {
  const col = tone === 'good' ? T.green : tone === 'bad' ? T.amber : T.muted2
  return (
    <span style={{ fontSize: 11, fontWeight: 700, color: col, background: T.card2,
      border: `1px solid ${T.border}`, borderRadius: 7, padding: '4px 8px', whiteSpace: 'nowrap' }}>
      <span style={{ color: T.muted, fontWeight: 400 }}>{label} </span>{value}
    </span>
  )
}

function TdCard({ r, onPlayer, inSlip, onToggle }) {
  const p = (r.prob || 0) / 100
  const open = () => onPlayer && onPlayer({
    gsis_id: r.gsis_id, full_name: r.name, pos: r.pos, team: r.team, opp: r.opp, headshot: r.headshot,
    market: 'Anytime TD', prob_over: p, edge_pct: r.edge_pct, book_over_odds: r.book_odds,
    note: r.note, status: r.status || 'active',
  })
  return (
    <button onClick={open} style={{ display: 'block', width: '100%', textAlign: 'left', cursor: 'pointer',
      color: T.text, background: T.card, border: `1px solid ${inSlip ? T.green : r.rank <= 3 ? T.greenMid : T.border}`,
      borderRadius: 12, padding: 13, marginBottom: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 9 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9, minWidth: 0 }}>
          <span style={{ fontSize: 14, fontWeight: 800, color: r.rank <= 3 ? T.green : T.muted, fontFamily: T.mono }}>#{r.rank}</span>
          <Avatar url={r.headshot} name={r.name} team={r.team} size={36} />
          <span style={{ minWidth: 0 }}>
            <span style={{ display: 'block', fontSize: 15, fontWeight: 800, lineHeight: 1.2 }}>{r.name}</span>
            <span style={{ display: 'block', fontSize: 11, color: T.muted }}>{r.pos} · {teamNick(r.team)} vs {teamNick(r.opp)}</span>
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontFamily: T.mono, fontWeight: 900, fontSize: 18, lineHeight: 1, color: tdColor(p) }}>{r.prob}%</div>
            <div style={{ fontSize: 10, color: T.muted, letterSpacing: '0.04em' }}>TO SCORE</div>
          </div>
          <span role="button" onClick={e => { e.stopPropagation(); onToggle && onToggle(r) }}
            title={inSlip ? 'Remove from parlay' : 'Add to parlay'}
            style={{ width: 30, height: 30, borderRadius: 8, flexShrink: 0, display: 'flex', alignItems: 'center',
              justifyContent: 'center', fontSize: 16, fontWeight: 900, cursor: 'pointer',
              background: inSlip ? T.green : T.greenDim, color: inSlip ? '#04140D' : T.green,
              border: `1px solid ${T.green}` }}>{inSlip ? '✓' : '+'}</span>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {r.gl_touches != null && <Sig label="Goal line" value={r.gl_touches} tone={r.gl_touches >= 3 ? 'good' : null} />}
        {r.rz_share != null && <Sig label="RZ share" value={`${r.rz_share}%`} tone={r.rz_share >= 20 ? 'good' : null} />}
        {r.opp_rz_td_rate != null && <Sig label={`${r.opp} RZ TD`} value={`${r.opp_rz_td_rate}%`} tone={r.opp_rz_td_rate >= 58 ? 'good' : r.opp_rz_td_rate <= 48 ? 'bad' : null} />}
        {r.def_td_rank != null && <Sig label="Def" value={`#${r.def_td_rank}`} tone={r.def_td_rank <= 10 ? 'good' : r.def_td_rank >= 23 ? 'bad' : null} />}
        {r.implied_pts != null && <Sig label="Team total" value={r.implied_pts} tone={r.implied_pts >= 24 ? 'good' : r.implied_pts <= 18 ? 'bad' : null} />}
        {r.pace != null && <Sig label="Pace" value={r.pace} tone={r.pace >= 64 ? 'good' : null} />}
        {r.wind != null && r.wind >= 12 && <Sig label="💨" value={`${Math.round(r.wind)}mph`} tone="bad" />}
        {r.edge_pct != null && Math.abs(r.edge_pct) >= 1 && <Sig label="Edge" value={`${r.edge_pct >= 0 ? '+' : ''}${r.edge_pct.toFixed(1)}%`} tone={r.edge_pct >= 0 ? 'good' : 'bad'} />}
      </div>
    </button>
  )
}

// ── Due (collapsible) ─────────────────────────────────────────────────────────
// Players the model still projects to score who've gone cold, with recent touches shown so
// a usage collapse never reads as "due." Positive-regression value, not the gambler's fallacy.
// Collapsed by default so it sits cleanly inside TD Lab under the Red Zone board.
const DUE_AMBER = '#F5A524'
function DueSection({ season, week, onPlayer }) {
  const [d, setD] = useState(null)
  const [open, setOpen] = useState(false)
  useEffect(() => { let a = true
    getNFLDueTds(season, week, 50, { minTouches: 6 }).then(r => { if (a) setD(r) }).catch(() => { if (a) setD({ due: [] }) })
    return () => { a = false } }, [season, week])
  const due = d?.due || []
  return (
    <div style={{ marginBottom: 12, border: `1px solid ${T.border}`, borderRadius: 14, background: T.card, overflow: 'hidden' }}>
      <button onClick={() => setOpen(o => !o)} style={{ display: 'flex', width: '100%', alignItems: 'center', gap: 8,
        cursor: 'pointer', background: 'transparent', border: 'none', color: T.text, padding: '13px 14px' }}>
        <span style={{ fontSize: 15, fontWeight: 900 }}>🔥 Due</span>
        <span style={{ fontSize: 11, color: T.muted, fontWeight: 700 }}>cold but still projected to score</span>
        <span style={{ marginLeft: 'auto', fontSize: 12, color: DUE_AMBER, fontWeight: 800 }}>
          {due.length ? `${due.length} ` : ''}{open ? '▴' : '▾'}</span>
      </button>
      {open && (
        <div style={{ padding: '0 12px 12px' }}>
          <div style={{ fontSize: 10.5, color: T.muted, margin: '0 0 9px', lineHeight: 1.45 }}>
            The touches are there, the finishes haven’t come — <b style={{ color: T.text }}>positive-regression value</b>, not
            “he’s due.” <span style={{ color: T.faint }}>COLD = games since last TD.</span></div>
          {!due.length
            ? <div style={{ fontSize: 11, color: T.muted, padding: '8px 0' }}>Needs a few games of history — fills in as the season banks weeks.</div>
            : due.map((r, i) => (
              <button key={r.gsis_id || i} onClick={() => onPlayer && onPlayer({ gsis_id: r.gsis_id, full_name: r.name, team: r.team, pos: r.pos, opp: r.opp, prop_type: 'anytime_td' })}
                style={{ display: 'flex', width: '100%', alignItems: 'center', gap: 10, textAlign: 'left', cursor: 'pointer',
                  background: T.card2, border: `1px solid ${T.border}`, borderRadius: 11, padding: '9px 11px', marginBottom: 7, color: T.text }}>
                <Avatar url={r.headshot} name={r.name} team={r.team} size={30} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 800, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {r.name} <span style={{ fontSize: 10, color: T.muted, fontWeight: 700 }}>{r.pos} · {teamNick(r.team)} vs {teamNick(r.opp)}</span></div>
                  <div style={{ fontSize: 10, color: T.muted, marginTop: 1, fontFamily: T.mono }}>
                    last TD {r.last_td} · {r.avg_touches} tch/g</div>
                </div>
                <div style={{ flex: 'none', textAlign: 'center' }}>
                  <div style={{ fontFamily: T.mono, fontWeight: 900, fontSize: 15, color: DUE_AMBER }}>{r.drought}g</div>
                  <div style={{ fontSize: 10, color: T.muted, fontWeight: 800 }}>COLD</div></div>
                <div style={{ fontFamily: T.mono, fontWeight: 900, fontSize: 16, color: tdColor((r.prob || 0) / 100), minWidth: 44, textAlign: 'right' }}>{r.prob}%</div>
              </button>
            ))}
        </div>
      )}
    </div>
  )
}

export default function TdLabTab({ season, week, onPlayer, onAddLeg, slipKeys, hideDue, gameFilter }) {
  const oddsRange = useOddsRange()
  const [rows, setRows] = useState(null)
  const [sort, setSort] = useState('prob')
  const [filters, setFilters] = useState({})
  const [pos, setPos] = useState('ALL')
  const [q, setQ] = useState('')

  useEffect(() => {
    let alive = true
    getNFLTdLab(season, week).then(d => { if (alive) setRows(d.rows || []) })
    return () => { alive = false }
  }, [season, week])

  // "+" adds an anytime-TD leg to the shared, mixed-market parlay slip.
  const addTd = r => onAddLeg && onAddLeg({ gsis_id: r.gsis_id, prop: 'anytime_td',
    market: 'Anytime TD', name: r.name, team: r.team, opp: r.opp, pos: r.pos })

  if (!rows) return <Spinner />

  const active = FILTERS.filter(([k]) => filters[k])
  const inSlip = slipKeys || new Set()
  const qMatch = searchMatch(q, r => [r.name, r.team, r.opp, r.pos])
  const shown = rows
    .filter(r => inGameFilter(gameFilter, r.team))
    .filter(r => inOddsRange(oddsRange, r.book_odds))
    .filter(r => pos === 'ALL' || (r.pos || '').toUpperCase() === pos)
    .filter(r => active.every(([, , fn]) => fn(r)))
    .filter(qMatch)
    .slice()
    .sort((a, b) => (b[sort] || 0) - (a[sort] || 0))
    .map((r, i) => ({ ...r, rank: i + 1 }))

  return (
    <div style={{ padding: '4px 16px 90px' }}>
      {/* Curated highlight board — the week's best red-zone scoring setups. */}
      <RedZoneSpots season={season} week={week} onPlayer={onPlayer} addTd={addTd} slipKeys={inSlip} gameFilter={gameFilter} />

      {/* Due — cold but still projected to score (collapsible). */}
      {/* Hidden inside the Touchdowns hub, which has Due as its own view. */}
      {!hideDue && <DueSection season={season} week={week} onPlayer={onPlayer} />}

      <div style={{ fontSize: 11, color: T.muted, marginBottom: 10, lineHeight: 1.4 }}>
        Every anytime-TD spot with the signals that decide it — opportunity · matchup · environment. Filter to the exact profile, tap for the full breakdown.
      </div>

      {/* position filter */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
        {POSITIONS.map(([k, lbl]) => {
          const on = k === pos
          return (
            <button key={k} onClick={() => setPos(k)}
              style={{ flex: 1, padding: '7px 0', borderRadius: 20, cursor: 'pointer',
                border: `1px solid ${on ? T.green : T.border}`, background: on ? T.green : T.card,
                color: on ? '#04140D' : T.muted, fontSize: 12, fontWeight: 800 }}>{lbl}</button>
          )
        })}
      </div>

      {/* filter chips */}
      <ScrollRow style={{ paddingBottom: 8, marginBottom: 4 }}>
        {FILTERS.map(([k, lbl]) => {
          const on = !!filters[k]
          return (
            <button key={k} onClick={() => setFilters(f => ({ ...f, [k]: !f[k] }))}
              style={{ flexShrink: 0, padding: '7px 12px', borderRadius: 20, cursor: 'pointer', whiteSpace: 'nowrap',
                border: `1px solid ${on ? T.green : T.border}`, background: on ? T.greenDim : T.card,
                color: on ? T.green : T.muted, fontSize: 12, fontWeight: 700 }}>{on ? '✓ ' : ''}{lbl}</button>
          )
        })}
      </ScrollRow>

      <SearchBar value={q} onChange={setQ} />

      {/* sort selector */}
      <ScrollRow style={{ paddingBottom: 8, marginBottom: 8, alignItems: 'center' }}>
        <span style={{ fontSize: 11, color: T.muted, alignSelf: 'center', flexShrink: 0 }}>Sort</span>
        {SORTS.map(([k, lbl]) => {
          const on = k === sort
          return (
            <button key={k} onClick={() => setSort(k)}
              style={{ flexShrink: 0, padding: '6px 11px', borderRadius: 8, cursor: 'pointer', whiteSpace: 'nowrap',
                border: `1px solid ${on ? T.mint : T.border}`, background: on ? T.card2 : T.card,
                color: on ? T.mint : T.muted, fontSize: 11.5, fontWeight: 700 }}>{lbl}</button>
          )
        })}
      </ScrollRow>

      <div style={{ fontSize: 10.5, color: T.muted, marginBottom: 8 }}>
        {shown.length} spots{active.length ? ' · filtered' : ''} · <span style={{ color: T.green }}>tap + to build a parlay</span>
      </div>
      {!shown.length ? <Empty message="No TD spots match — clear a filter, or check back near kickoff as the week's board fills in." />
        : shown.map(r => <TdCard key={r.gsis_id} r={r} onPlayer={onPlayer}
            inSlip={inSlip.has(`${r.gsis_id}:anytime_td`)} onToggle={addTd} />)}
    </div>
  )
}
